import PropTypes from "prop-types";
import { Anchor, Box, Flex, Image, Text, useMantineTheme } from "@mantine/core";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { BorderedContent } from "./BorderedContent";
import React from "react";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import {
  formatTimeStamp,
  SHORT_DISPLAY_FORMAT,
} from "../utils/collectionFound/dateTimeUtils";
import { formatHexStr } from "../utils/formatHexStr";

const StampDetailCollectionFound = ({ data, verified, staticBasePath }) => {
  //css classes
  const { classes: tooltipClasses } = useStylesTooltip();

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  return (
    <BorderedContent>
      <Box className="details">
        <Box className="tooltip">
          <CustomTooltip
            content={
              <>
                <Text
                  classNames={{
                    root: tooltipClasses.tooltipText,
                  }}
                >
                  The details below are for the Collection specified in the
                  Submitted Data.
                </Text>

                <Text
                  classNames={{
                    root: tooltipClasses.tooltipText,
                  }}
                >
                  A Collection is a group of vBase Stamps created by the
                  Collection Owner. One can think of a Collection as a folder or
                  directory that attributes vBase Stamps to specific projects,
                  datasets, strategies, experiments, etc.
                </Text>

                <Text
                  classNames={{
                    root: tooltipClasses.tooltipText,
                  }}
                >
                  The Collection Name is specified at creation by the Collection
                  Owner. The Collection ID is a fingerprint of the Collection
                  Name. To read more about Collections,{" "}
                  <Anchor
                    underline="always"
                    c={theme.customColors.blackLight}
                    href="https://docs.vbase.com/"
                    target="_blank"
                  >
                    visit our docs
                  </Anchor>
                  .
                </Text>
              </>
            }
          ></CustomTooltip>
        </Box>

        <Flex
          className="info"
          justify={theme.flex.justify.between}
          mt={theme.margin.negativeXs}
        >
          <Flex gap={theme.flex.gap.xxs}>
            <Text className="key" component="span">
              User
            </Text>

            <CustomAnchor
              text={data.user_name || data.user_address}
              componentClasses="value stamp-link stamp-user"
              color={theme.customColors.black}
              href={`/verify/user-data/?user=${
                data.user_name || data.user_address
              }`}
            />
          </Flex>
        </Flex>
        <Flex className="info" justify={theme.flex.justify.between}>
          <Flex gap={theme.flex.gap.xxs}>
            <Text className="key" component="span">
              User Identity
            </Text>

            {verified?.is_verified ? (
              <Flex
                gap={theme.flex.gap.xs}
                align={theme.flex.align.center}
                className="value"
              >
                <Image
                  src={`${staticBasePath}public/images/svg/verified.svg`}
                  alt="verified"
                />
                Verified{" "}
                {verified?.verified_since && (
                  <Text component="span">
                    (since{" "}
                    {formatTimeStamp(
                      verified.verified_since,
                      data.display_timezone,
                      SHORT_DISPLAY_FORMAT,
                    )}
                    )
                  </Text>
                )}
              </Flex>
            ) : (
              <Flex
                gap={theme.flex.gap.xs}
                align={theme.flex.align.center}
                className="value"
              >
                <Image
                  src={`${staticBasePath}public/images/svg/ellips.svg`}
                  alt="verified"
                />
                Not Verified
              </Flex>
            )}
          </Flex>
        </Flex>

        <Flex className="info" justify={theme.flex.justify.between}>
          <Flex gap={theme.flex.gap.xxs}>
            <Text className="key" component="span">
              Collection
            </Text>
            {data.collection_name ? (
              <CustomAnchor
                text={data.collection_name}
                componentClasses="value collection-name stamp-link"
                color={theme.customColors.black}
                href={`/verify/user-data/?user=${
                  data.user_name || data.user_address
                }&collection_name=${data.collection_name}`}
              />
            ) : data.collection_id ? (
              <CustomAnchor
                text={formatHexStr(data.collection_id)}
                componentClasses="value collection-name stamp-link"
                color={theme.customColors.black}
                href={`/verify/user-data/?user=${
                  data.user_name || data.user_address
                }&collection_cid=${data.collection_id}`}
              />
            ) : (
              <Text component="span" className="value collection-name">
                {"<<None>>"}
              </Text>
            )}
          </Flex>
        </Flex>
      </Box>
    </BorderedContent>
  );
};

StampDetailCollectionFound.propTypes = {
  data: PropTypes.object.isRequired,
  staticBasePath: PropTypes.string.isRequired,
  verified: PropTypes.object.isRequired,
};
export default StampDetailCollectionFound;
